import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminBlogAPI, adminCategoryAPI } from '../services/api';
import '../styles/Modal.css';

const BlogModal = ({ isOpen, onClose, onSuccess, blog = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    author: 'EC Biolabs Team',
    featuredImage: '',
    excerpt: '',
    content: '',
    readTime: 5,
    tags: [],
    status: 'Draft',
    featured: false,
    metaTitle: '',
    metaDescription: ''
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Rich text editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      [{ 'color': [] }, { 'background': [] }],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link', 'image',
    'color', 'background',
    'blockquote', 'code-block'
  ];

  // Fetch product categories when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      if (blog) {
        setFormData({
          title: blog.title || '',
          category: blog.category || '',
          author: blog.author || 'EC Biolabs Team',
          featuredImage: blog.featuredImage || '',
          excerpt: blog.excerpt || '',
          content: blog.content || '',
          readTime: blog.readTime || 5,
          tags: blog.tags || [],
          status: blog.status || 'Draft',
          featured: blog.featured || false,
          metaTitle: blog.metaTitle || '',
          metaDescription: blog.metaDescription || ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, blog]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await adminCategoryAPI.getAll();
      console.log("response",response)
      setCategories(response.data.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      author: 'EC Biolabs Team',
      featuredImage: '',
      excerpt: '',
      content: '',
      readTime: 5,
      tags: [],
      status: 'Draft',
      featured: false,
      metaTitle: '',
      metaDescription: ''
    });
    setTagInput('');
    setError('');
    setFieldErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value
    }));
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const submitData = {
        ...formData,
        readTime: parseInt(formData.readTime) || 5
      };

      if (blog) {
        await adminBlogAPI.update(blog._id, submitData);
      } else {
        await adminBlogAPI.create(submitData);
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      console.error('Error saving blog:', err);

      // Handle validation errors
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const errors = {};
        err.response.data.errors.forEach(error => {
          errors[error.field] = error.message;
        });
        setFieldErrors(errors);
        setError(err.response?.data?.message || 'Please fix the errors below');
      } else {
        setError(err.response?.data?.message || 'Failed to save blog');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal blog-modal">
        <div className="modal-header">
          <h2>{blog ? 'Edit Blog' : 'Create New Blog'}</h2>
          <button className="close-btn" onClick={onClose} disabled={loading}>
            &times;
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter blog title"
                className={fieldErrors.title ? 'error' : ''}
              />
              {fieldErrors.title && (
                <small className="error-text">{fieldErrors.title}</small>
              )}
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                disabled={loadingCategories}
                className={fieldErrors.category ? 'error' : ''}
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select a category'}
                </option>
                {categories.map((category) => (
                  <option key={category._id} value={category.title}>
                    {category.title}
                  </option>
                ))}
              </select>
              {fieldErrors.category && (
                <small className="error-text">{fieldErrors.category}</small>
              )}
            </div>

            <div className="form-group">
              <label>Author</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
              />
            </div>

            <div className="form-group">
              <label>Read Time (minutes)</label>
              <input
                type="number"
                name="readTime"
                value={formData.readTime}
                onChange={handleChange}
                min="1"
                placeholder="5"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Draft">Draft</option>
                <option value="Published">Published</option>
                <option value="Archived">Archived</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                />
                Featured Blog
              </label>
            </div>

            <div className="form-group full-width">
              <label>Featured Image URL</label>
              <input
                type="url"
                name="featuredImage"
                value={formData.featuredImage}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
                className={fieldErrors.featuredImage ? 'error' : ''}
              />
              {fieldErrors.featuredImage && (
                <small className="error-text">{fieldErrors.featuredImage}</small>
              )}
            </div>

            <div className="form-group full-width">
              <label>Excerpt *</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                required
                rows="3"
                maxLength="300"
                placeholder="Short description (max 300 characters)"
                className={fieldErrors.excerpt ? 'error' : ''}
              />
              {fieldErrors.excerpt ? (
                <small className="error-text">{fieldErrors.excerpt}</small>
              ) : (
                <small>{formData.excerpt.length}/300 characters</small>
              )}
            </div>

            <div className="form-group full-width">
              <label>Content *</label>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter blog content with rich formatting..."
                className={fieldErrors.content ? 'rich-text-editor error' : 'rich-text-editor'}
              />
              {fieldErrors.content ? (
                <small className="error-text">{fieldErrors.content}</small>
              ) : (
                <small>Use the toolbar above to format your content. Supports headings, lists, links, images, and more.</small>
              )}
            </div>

            <div className="form-group full-width">
              <label>Tags</label>
              <div className="tags-input-wrapper">
                <div className="tags-display">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="tag-item">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="tag-remove"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
                <div className="tag-input-group">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add a tag and press Enter"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="btn-add-tag"
                  >
                    Add Tag
                  </button>
                </div>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Meta Title (SEO)</label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                placeholder="SEO meta title"
              />
            </div>

            <div className="form-group full-width">
              <label>Meta Description (SEO)</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                rows="2"
                placeholder="SEO meta description"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : blog ? 'Update Blog' : 'Create Blog'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogModal;
